/**
 * Helpers for the Flyimg image proxy (https://img.racefriends.tripnride.com.br).
 *
 * Flyimg resizes and re-compresses a remote source image and caches the result
 * on disk. Two behaviours of that cache matter to callers:
 *
 * 1. The cache key is the transform + source path only — the query string of the
 *    proxy request is ignored. A `?t=<timestamp>` cache-buster therefore never
 *    busts anything on Flyimg; it only forces a brand new (billable) Vercel
 *    image transformation on every page load. Use `imageProxyUrl`, which strips
 *    those busters.
 *
 * 2. Flyimg content-negotiates the output format from the `Accept` header
 *    (jpeg / webp / avif), caching each format separately. A cache entry that
 *    got written truncated is served forever as `200` with an empty body, which
 *    Vercel's optimizer reports as `502 OPTIMIZED_EXTERNAL_IMAGE_REQUEST_INVALID`.
 *    Rendering proxy URLs with `unoptimized` keeps Vercel out of the path (the
 *    image is already resized, so re-optimizing only adds a hop and a cost), and
 *    `imageProxyRefreshUrl` regenerates a single poisoned entry.
 */

export const IMAGE_PROXY_HOST = 'img.racefriends.tripnride.com.br';
export const IMAGE_PROXY_BASE = `https://${IMAGE_PROXY_HOST}/upload`;

/** Query params that only exist to bust a cache and are pointless on Flyimg. */
const CACHE_BUSTER_PARAMS = ['t', '_'];

function stripCacheBusters(sourceUrl: string): string {
  const queryStart = sourceUrl.indexOf('?');
  if (queryStart === -1) return sourceUrl;

  const path = sourceUrl.slice(0, queryStart);
  const params = sourceUrl
    .slice(queryStart + 1)
    .split('&')
    .filter((pair) => pair !== '')
    .filter((pair) => !CACHE_BUSTER_PARAMS.includes(pair.split('=')[0]));

  return params.length > 0 ? `${path}?${params.join('&')}` : path;
}

/**
 * Build a Flyimg URL.
 *
 * @param transform Flyimg options, e.g. `h_200`, `w_100`, `h_40,q_80`.
 * @param sourceUrl Absolute URL of the source image (usually `pb.files.getUrl`).
 */
export function imageProxyUrl(transform: string, sourceUrl: string): string {
  if (!sourceUrl) return '';
  return `${IMAGE_PROXY_BASE}/${transform}/${stripCacheBusters(sourceUrl)}`;
}

/** True when `url` is served by the image proxy. */
export function isImageProxyUrl(url?: string | null): boolean {
  return typeof url === 'string' && url.startsWith(`${IMAGE_PROXY_BASE}/`);
}

/**
 * Same URL with Flyimg's `rf_1` (refresh) flag, which discards the cached
 * variant and re-renders it. Use it to repair an entry that got cached empty
 * or corrupted.
 */
export function imageProxyRefreshUrl(proxyUrl: string): string {
  if (!isImageProxyUrl(proxyUrl)) return proxyUrl;

  const rest = proxyUrl.slice(`${IMAGE_PROXY_BASE}/`.length);
  const separator = rest.indexOf('/');
  if (separator === -1) return proxyUrl;

  const transform = rest.slice(0, separator);
  const source = rest.slice(separator + 1);
  if (transform.split(',').includes('rf_1')) return proxyUrl;

  return `${IMAGE_PROXY_BASE}/${transform},rf_1/${source}`;
}

/**
 * Flyimg URL for `sourceUrl`, falling back to the original when the proxy
 * cannot fetch it.
 *
 * The proxy runs on a public VM and downloads the source itself, so a local
 * PocketBase (`http://localhost:8090`) is unreachable from it. Anything not
 * served over https is therefore left untouched instead of rendering broken.
 */
export function optimizedImageUrl(
  transform: string,
  sourceUrl?: string | null,
): string {
  if (!sourceUrl) return '';
  if (!sourceUrl.startsWith('https://')) return sourceUrl;
  return imageProxyUrl(transform, sourceUrl);
}

/**
 * Transform for a stage cover rendered inside a card.
 *
 * 800px wide covers a full-width phone card at 2x and the half-width desktop
 * card, and `q_80` takes a typical 620KB upload down to ~90KB of WebP. The home
 * hero and the events list deliberately share this constant: same transform +
 * same source means one Flyimg cache entry and one browser cache entry, so
 * opening either screen warms the other.
 */
export const STAGE_COVER_TRANSFORM = 'w_800,q_80';

/**
 * Transform for the stage detail hero, which is wider than a card: full-bleed
 * on a phone and up to ~1100px inside the desktop card. 1200px keeps it sharp
 * on both without paying for the 1600px+ banner Sympla serves.
 */
export const STAGE_HERO_TRANSFORM = 'w_1200,q_80';
